import { typographyTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import { Form } from "radix-ui";
import { TextField } from "@radix-ui/themes";

export type InputFieldProps = {
  name: string;
  type?: any;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  required?: boolean;
  className?: string;
  prefix?: React.ReactNode;
  endAdornment?: React.ReactNode; // e.g., clear button
  error?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  autoComplete?: string;
  "data-testid"?: string;
  helperText?: string;
};

function InputField({
  name,
  label,
  placeholder,
  type = "text",
  value,
  onChange,
  required,
  className,
  prefix,
  endAdornment,
  helperText,
  error,
  inputRef,
  onBlur,
  onKeyDown,
  autoComplete,
  "data-testid": dataTestId,
}: InputFieldProps) {
  return (
    <Form.Field name={name} className={cn("w-full", className)}>
      {/* Keep label accessible but visually hidden unless explicitly passed */}
      {label ? <Form.Label className="sr-only">{label}</Form.Label> : null}
      <div className="relative">
        <Form.Control asChild>
          <TextField.Root
            ref={inputRef}
            name={name}
            type={type}
            value={value}
            onChange={(e) => {
              onChange?.(e);
            }}
            onBlur={(e) => {
              onBlur?.(e);
            }}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            required={required}
            autoComplete={autoComplete}
            data-testid={dataTestId}
            className={cn(endAdornment && "pr-10")}
          >
            {prefix && (
              <TextField.Slot className="bg-[--gray-3] mr-2 rounded-l-[inherit]">
                <span className="text-sm select-none">{prefix}</span>
              </TextField.Slot>
            )}
          </TextField.Root>
        </Form.Control>
        {endAdornment && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {endAdornment}
          </div>
        )}
      </div>
      {error && (
        <p className={cn(typographyTokens.helperText, "mt-1 text-red-600")}>
          {error}
        </p>
      )}
      {!error && helperText && (
        <p className={cn(typographyTokens.helperText, "mt-1 text-gray-600")}>
          {helperText}
        </p>
      )}
    </Form.Field>
  );
}

export default InputField;
